/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { ProgressIndicatorProps } from '../../../ProgressIndicator/ProgressIndicatorManager';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './ProgressIndicatorColumn';

const ProgressIndicatorColumnManager: ComponentManagerFn<
  'ProgressIndicatorColumn'
> = (platform: Platform): ComponentManager<'ProgressIndicatorColumn'> => {
  return {
    ...baseComponentManager<'ProgressIndicatorColumn'>(
      platform,
      'ProgressIndicatorColumn',
    ),
    type: 'ProgressIndicatorColumn',
    name: 'Progress Indicator Column',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnCellClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      ...ProgressIndicatorProps,
    },
    isPropertyVisible(prop, { type }, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({
          appUid,
          platform,
          itemId,
          pageId,
          prop,
        })
      ) {
        return false;
      }
      switch (prop) {
        case 'alignment':
        case 'bgColorExpression':
        case 'colorExpression':
          return false;
        case 'size':
        case 'thickness':
        case 'fontSize':
          return type === 'circular';
        case 'linearWidth':
          return false; // column width will be used
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    replaceableWith: ['StarColumn', 'NumberColumn'],
    category: 'Data Grid',
    icon: { iconName: 'percentage' },
    description: `**Overview**<br/>
The Progress Indicator Column is a visual Data Grid column that renders numeric values as graphical progress bars or circular gauges.
Instead of displaying raw numbers (e.g., 0.75 or 75%), it provides an immediate visual representation of completion, capacity, or status directly within the grid row. It is widely used for Project Tracking (Task Completion), Sales Dashboards (Quota Attainment), or Inventory Management (Stock Levels).

**1. Visual Styles**<br/>
You can choose how the progress is represented to fit your grid's density and layout.
* **Type:**
    * **Linear:** Renders a horizontal bar. The bar automatically expands to fill the width of the column. (Best For: Comparing relative values across rows, e.g., "Budget Used").
    * **Circular:** Renders a radial ring (donut chart style). Remains a fixed size centered in the cell. (Best For: Status badges or compact scores, e.g., "Health Score").

**2. Configuration & Customization**<br/>
This column inherits most properties from the standalone Progress Indicator component.
* **Data Binding:** Binds to a numeric attribute (Integer, Decimal, Percent). Values are typically interpreted as 0.0 to 1.0 (0% to 100%) or 0 to 100.
* **Styling:**
    * **Color:** The fill color of the progress bar (e.g., Success green or Primary blue).
    * **Thickness:** Controls the stroke weight of the bar or ring.
    * **Size:** (Circular only) Controls the diameter of the ring.

**3. Interaction**<br/>
While primarily for display, the column allows user interaction.
* **TriggerOnCellClick:** You can add this child component to execute logic when a user clicks the progress bar. (Use Case: Click a "Project Progress" bar to open a detailed Gantt chart or task list for that specific project).

**4. Best Practices**<br/>
* **Context:** It is often helpful to place a **Number Column** (displaying the exact percentage) next to the Progress Indicator Column if precise values are needed for sorting or reporting.
* **Linear vs. Circular:** Use **Linear** for grids with many columns where vertical space is tight. Use **Circular** if you have taller rows (e.g., 60px+) to accommodate the ring without shrinking it too much.`,
  };
};

export default ProgressIndicatorColumnManager;
